import { useCallback, useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';

type OverlayTabEvent = Pick<ReactKeyboardEvent<HTMLElement>, 'key' | 'shiftKey' | 'defaultPrevented' | 'preventDefault'> & { currentTarget: HTMLElement };

/** Stop boundary Tab from reaching browser chrome before the vendor focus lock
 * receives a focusin event. Interior navigation and portalled pickers stay native. */
export function cycleOverlayTab(event: OverlayTabEvent) {
  if (event.key !== 'Tab' || event.defaultPrevented) return;
  const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('button,input,select,textarea,a[href],[tabindex],[contenteditable="true"]'))
    .filter(node => node.tabIndex >= 0 && !node.matches(':disabled') && node.getClientRects().length > 0
      && !node.closest('[hidden],[inert],[aria-hidden="true"]') && getComputedStyle(node).visibility !== 'hidden');
  const first = controls[0], last = controls.at(-1);
  if (first && last && (event.shiftKey ? document.activeElement === first : document.activeElement === last)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  }
}

/** Let an existing controlled dialog reuse the same boundary handling without
 * replacing its presentation or adding a second focus trap. */
export function useOverlayTabBoundary() {
  const cleanup = useRef<(() => void) | undefined>(undefined);
  useEffect(() => () => cleanup.current?.(), []);
  return useCallback((content: HTMLElement | null) => {
    cleanup.current?.();
    cleanup.current = undefined;
    const dialog = content?.closest<HTMLElement>('[role="dialog"]');
    if (!dialog) return;
    const onKeyDown = (event: KeyboardEvent) => cycleOverlayTab({
      key: event.key, shiftKey: event.shiftKey, defaultPrevented: event.defaultPrevented,
      currentTarget: dialog, preventDefault: () => event.preventDefault(),
    });
    dialog.addEventListener('keydown', onKeyDown);
    cleanup.current = () => dialog.removeEventListener('keydown', onKeyDown);
  }, []);
}
